import { useEffect, useMemo, useState, useCallback, useRef, createContext, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, { Background, Controls, ReactFlowProvider, Handle, Position, addEdge } from "reactflow";
import "reactflow/dist/style.css";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { FolderSelect } from "@/components/FolderSelect";
import { MovementForm } from "@/components/MovementForm";
import { computeTotals, fmtMoney, CURRENCY_SYMBOL, parseLocalDate } from "@/lib/movemap";
import { computeLedger } from "@/lib/ledger";
import { useTheme } from "@/context/ThemeContext";
import { PAGE, BTN_PRIMARY, ICON_BTN } from "@/lib/styles";
import { ArrowLeft, Save, Plus, Trash2, Wallet, Type, Sigma, X, ListTree, Landmark, Pencil, Info, ChevronDown, Scale, FileText, Table2, Network, ExternalLink, Link2, ArrowLeftRight, Copy, CopyPlus, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";

const BoardCtx = createContext(null);
const uid = () => "b_" + Math.random().toString(36).slice(2, 10);
const TOTAL_ID = "total-general";

const DOC_META = {
  movemap: { label: "Flujo económico", Icon: Wallet, color: "#f97316", route: (id) => `/movimientos/${id}` },
  ledger: { label: "Mapa Contable", Icon: Scale, color: "#10b981", route: (id) => `/contable/${id}` },
  sheet: { label: "Hoja de cálculo", Icon: Table2, color: "#10b981", route: (id) => `/hoja/${id}` },
  text: { label: "Nota", Icon: FileText, color: "#0ea5e9", route: (id) => `/note/${id}` },
  mindmap: { label: "Mapa mental", Icon: Network, color: "#6366f1", route: (id) => `/mindmap/${id}` },
};

const srcHandle = <Handle id="out" type="source" position={Position.Right} className="!bg-slate-400 !w-3 !h-3 !border-2 !border-white dark:!border-slate-900" />;
const tgtHandle = (color) => <Handle id="in" type="target" position={Position.Left} className="!w-3 !h-3 !border-2 !border-white dark:!border-slate-900" style={{ background: color }} />;

function MoneyRows({ t, dc }) {
  return (
    <div className="px-3 py-2 space-y-1">
      <div className="flex justify-between text-xs"><span className="text-emerald-600 dark:text-emerald-400">Ingresos</span><span className="font-mono2">{fmtMoney(t.totalIn, dc)}</span></div>
      <div className="flex justify-between text-xs"><span className="text-rose-600 dark:text-rose-400">Egresos</span><span className="font-mono2">{fmtMoney(t.totalEg, dc)}</span></div>
      <div className="flex justify-between text-xs pt-1 border-t border-slate-100 dark:border-slate-800"><span className="font-medium">Haber</span><span className={`font-mono2 font-semibold ${t.haber >= 0 ? "text-slate-800 dark:text-slate-100" : "text-rose-600"}`}>{fmtMoney(t.haber, dc)}</span></div>
    </div>
  );
}

function FlujoNode({ id, data }) {
  const ctx = useContext(BoardCtx);
  const t = ctx.totals[id] || { totalIn: 0, totalEg: 0, haber: 0, count: 0 };
  return (
    <div className="rounded-xl border border-orange-400 dark:border-orange-500/60 bg-white dark:bg-slate-900 shadow-md w-60" data-testid={`board-flujo-${id}`}>
      {tgtHandle("#f97316")}{srcHandle}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-800">
        <Wallet size={14} className="text-orange-500 shrink-0" />
        <input value={data.title} onChange={(e) => ctx.updateBlock(id, { title: e.target.value })} className="nodrag flex-1 min-w-0 bg-transparent font-heading font-medium text-sm focus:outline-none" placeholder="Nombre (ej. Casa)" data-testid={`board-flujo-title-${id}`} />
        <button onClick={(e) => { e.stopPropagation(); ctx.duplicateBlock(id); }} className="text-slate-400 hover:text-indigo-500 nodrag shrink-0" title="Duplicar" data-testid={`board-flujo-dup-${id}`}><Copy size={13} /></button>
        <button onClick={(e) => { e.stopPropagation(); ctx.removeBlock(id); }} className="text-slate-400 hover:text-rose-500 nodrag shrink-0" data-testid={`board-flujo-del-${id}`}><Trash2 size={13} /></button>
      </div>
      <MoneyRows t={t} dc={ctx.displayCurrency} />
      <button onClick={(e) => { e.stopPropagation(); ctx.openMovements(id); }} className="nodrag w-full flex items-center justify-center gap-1.5 px-3 py-2 border-t border-slate-100 dark:border-slate-800 text-xs text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-500/10 rounded-b-xl" data-testid={`board-flujo-movements-${id}`}>
        <ListTree size={13} /> Movimientos ({t.count || 0})
      </button>
    </div>
  );
}

function EmbedNode({ id, data }) {
  const ctx = useContext(BoardCtx);
  const meta = DOC_META[data.docType] || DOC_META.text;
  const Icon = meta.Icon;
  const t = ctx.totals[id];
  const numeric = !!t;
  return (
    <div className="rounded-xl border bg-white dark:bg-slate-900 shadow-md w-60" style={{ borderColor: meta.color }} data-testid={`board-embed-${id}`}>
      {tgtHandle(meta.color)}{srcHandle}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-800">
        <Icon size={14} style={{ color: meta.color }} className="shrink-0" />
        <span className="flex-1 min-w-0 font-heading font-medium text-sm truncate">{data.title}</span>
        <span className="text-[9px] uppercase tracking-wide text-slate-400 shrink-0">{meta.label}</span>
        <button onClick={(e) => { e.stopPropagation(); ctx.duplicateBlock(id); }} className="text-slate-400 hover:text-indigo-500 nodrag shrink-0" title="Duplicar" data-testid={`board-embed-dup-${id}`}><Copy size={13} /></button>
        <button onClick={(e) => { e.stopPropagation(); ctx.removeBlock(id); }} className="text-slate-400 hover:text-rose-500 nodrag shrink-0" data-testid={`board-embed-del-${id}`}><Trash2 size={13} /></button>
      </div>
      {numeric && data.docType === "ledger" ? (
        <div className="px-3 py-2 space-y-1">
          <div className="flex justify-between text-xs"><span className="text-slate-500">Patrimonio (A−P)</span><span className={`font-mono2 font-semibold ${t.haber >= 0 ? "text-slate-800 dark:text-slate-100" : "text-rose-600"}`}>{fmtMoney(t.haber, ctx.displayCurrency)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-slate-500">Resultado (I−G)</span><span className="font-mono2">{fmtMoney(t.totalIn - t.totalEg, ctx.displayCurrency)}</span></div>
        </div>
      ) : numeric ? (
        <MoneyRows t={t} dc={ctx.displayCurrency} />
      ) : (
        <div className="px-3 py-2 text-[11px] text-slate-400">Documento incrustado (sin cálculo numérico).</div>
      )}
      <button onClick={(e) => { e.stopPropagation(); window.open(meta.route(data.docId), "_blank"); }} className="nodrag w-full flex items-center justify-center gap-1.5 px-3 py-2 border-t border-slate-100 dark:border-slate-800 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 rounded-b-xl" style={{ color: meta.color }} data-testid={`board-embed-open-${id}`}>
        <ExternalLink size={13} /> Abrir para editar
      </button>
    </div>
  );
}

function TotalNode() {
  const ctx = useContext(BoardCtx);
  const dc = ctx.displayCurrency;
  const g = ctx.grandTotal;
  return (
    <div className="rounded-2xl border-2 border-emerald-500 dark:border-emerald-500/70 bg-white dark:bg-slate-900 shadow-xl w-64" data-testid="board-total-general">
      {tgtHandle("#10b981")}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 bg-emerald-50 dark:bg-emerald-500/10 rounded-t-2xl">
        <Landmark size={16} className="text-emerald-600 dark:text-emerald-400" />
        <span className="font-heading font-semibold text-sm">Total General</span>
        <span className="ml-auto text-[10px] text-slate-400">{g.count} bloque(s)</span>
      </div>
      <div className="px-4 py-3 space-y-1.5">
        <div className="flex justify-between text-sm"><span className="text-emerald-600 dark:text-emerald-400">Ingresos</span><span className="font-mono2" data-testid="board-total-in">{fmtMoney(g.totalIn, dc)}</span></div>
        <div className="flex justify-between text-sm"><span className="text-rose-600 dark:text-rose-400">Egresos</span><span className="font-mono2" data-testid="board-total-eg">{fmtMoney(g.totalEg, dc)}</span></div>
        <div className="flex justify-between text-base pt-2 border-t border-slate-100 dark:border-slate-800">
          <span className="font-semibold">Haber</span>
          <span className={`font-mono2 font-bold ${g.haber >= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600"}`} data-testid="board-total-haber">{fmtMoney(g.haber, dc)}</span>
        </div>
      </div>
      {g.count === 0 && <p className="px-4 pb-3 text-[11px] text-slate-400">Conecta tus bloques aquí (arrastra desde el punto derecho hasta este nodo).</p>}
    </div>
  );
}

function TextNode({ id, data }) {
  const ctx = useContext(BoardCtx);
  return (
    <div className="rounded-xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 shadow-md w-56 p-2" data-testid={`board-text-${id}`}>
      {tgtHandle("#f59e0b")}{srcHandle}
      <div className="flex justify-end">
        <button onClick={(e) => { e.stopPropagation(); ctx.removeBlock(id); }} className="text-slate-400 hover:text-rose-500 nodrag" data-testid={`board-text-del-${id}`}><X size={13} /></button>
      </div>
      <textarea value={data.text} onChange={(e) => ctx.updateBlock(id, { text: e.target.value })} placeholder="Escribe una nota..." className="nodrag w-full bg-transparent text-sm resize-none focus:outline-none min-h-[60px]" data-testid={`board-text-input-${id}`} />
    </div>
  );
}

function OpNode({ id, data }) {
  const ctx = useContext(BoardCtx);
  const dc = ctx.displayCurrency;
  const field = data.field || "haber";
  const op = data.operation || "subtract";
  const members = ctx.membersOf(id);
  const values = members.map((b) => (ctx.totals[b.id] || {})[field] || 0);
  const result = values.length
    ? (op === "sum" ? values.reduce((a, b) => a + b, 0)
      : op === "subtract" ? values.reduce((a, b) => a - b)
      : op === "avg" ? values.reduce((a, b) => a + b, 0) / values.length : 0)
    : 0;
  const FIELD_LABEL = { haber: "Haber", totalIn: "Ingresos", totalEg: "Egresos" };
  return (
    <div className="rounded-xl border-2 border-indigo-400 dark:border-indigo-500/60 bg-white dark:bg-slate-900 shadow-lg w-60" data-testid={`board-op-${id}`}>
      {tgtHandle("#6366f1")}{srcHandle}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-800 bg-indigo-50 dark:bg-indigo-500/10 rounded-t-xl">
        <span className="flex items-center gap-1.5 font-heading font-medium text-sm"><Sigma size={14} className="text-indigo-500" /> Operación</span>
        <button onClick={(e) => { e.stopPropagation(); ctx.duplicateBlock(id); }} className="text-slate-400 hover:text-indigo-500 nodrag" title="Duplicar" data-testid={`board-op-dup-${id}`}><Copy size={13} /></button>
        <button onClick={(e) => { e.stopPropagation(); ctx.removeBlock(id); }} className="text-slate-400 hover:text-rose-500 nodrag" data-testid={`board-op-del-${id}`}><Trash2 size={13} /></button>
      </div>
      <div className="px-3 py-2 space-y-2">
        <div className="flex gap-2">
          <select value={op} onChange={(e) => ctx.updateBlock(id, { operation: e.target.value })} className="nodrag flex-1 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1" data-testid={`board-op-operation-${id}`}>
            <option value="sum">Sumar (+)</option><option value="subtract">Restar (−)</option><option value="avg">Promedio</option>
          </select>
          <select value={field} onChange={(e) => ctx.updateBlock(id, { field: e.target.value })} className="nodrag flex-1 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1" data-testid={`board-op-field-${id}`}>
            <option value="haber">Haber</option><option value="totalIn">Ingresos</option><option value="totalEg">Egresos</option>
          </select>
        </div>
        <p className="text-[10px] text-slate-400 truncate">{members.length === 0 ? "Conecta bloques a este nodo." : members.map((m) => m.title || "(sin nombre)").join(op === "sum" ? " + " : op === "subtract" ? " − " : ", ")}</p>
        <div className="pt-2 border-t border-slate-100 dark:border-slate-800 text-center">
          <p className="text-[10px] uppercase tracking-wide text-slate-400">{FIELD_LABEL[field]} · {op === "sum" ? "suma" : op === "subtract" ? "resta" : "promedio"}</p>
          <p className={`font-mono2 text-lg font-bold ${result >= 0 ? "text-indigo-600 dark:text-indigo-300" : "text-rose-600"}`} data-testid={`board-op-result-${id}`}>{fmtMoney(result, dc)}</p>
        </div>
      </div>
    </div>
  );
}

function CambioNode({ id, data }) {
  const ctx = useContext(BoardCtx);
  const set = (patch) => ctx.updateBlock(id, patch);
  return (
    <div className="rounded-xl border border-sky-300 dark:border-sky-500/50 bg-sky-50 dark:bg-sky-950/40 shadow-md w-56" data-testid={`board-cambio-${id}`}>
      {tgtHandle("#0ea5e9")}{srcHandle}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-sky-100 dark:border-sky-800/60">
        <ArrowLeftRight size={14} className="text-sky-500 shrink-0" />
        <input value={data.concepto || ""} onChange={(e) => set({ concepto: e.target.value })} className="nodrag flex-1 min-w-0 bg-transparent font-heading font-medium text-sm focus:outline-none" placeholder="Cambio de moneda" data-testid={`board-cambio-title-${id}`} />
        <button onClick={(e) => { e.stopPropagation(); ctx.duplicateBlock(id); }} className="text-slate-400 hover:text-indigo-500 nodrag shrink-0" title="Duplicar" data-testid={`board-cambio-dup-${id}`}><Copy size={13} /></button>
        <button onClick={(e) => { e.stopPropagation(); ctx.removeBlock(id); }} className="text-slate-400 hover:text-rose-500 nodrag shrink-0" data-testid={`board-cambio-del-${id}`}><Trash2 size={13} /></button>
      </div>
      <div className="px-3 py-2 space-y-1.5">
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-slate-400 w-14">Entregas</span>
          <input type="number" value={data.monto ?? ""} onChange={(e) => set({ monto: Number(e.target.value) })} className="nodrag flex-1 min-w-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5 text-right font-mono2 text-xs" data-testid={`board-cambio-from-${id}`} />
          <select value={data.moneda || "USD"} onChange={(e) => set({ moneda: e.target.value })} className="nodrag bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1 py-0.5 text-xs"><option value="USD">$</option><option value="VES">Bs</option></select>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-slate-400 w-14">Recibes</span>
          <input type="number" value={data.montoDestino ?? ""} onChange={(e) => set({ montoDestino: Number(e.target.value) })} className="nodrag flex-1 min-w-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5 text-right font-mono2 text-xs" data-testid={`board-cambio-to-${id}`} />
          <select value={data.monedaDestino || "VES"} onChange={(e) => set({ monedaDestino: e.target.value })} className="nodrag bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1 py-0.5 text-xs"><option value="USD">$</option><option value="VES">Bs</option></select>
        </div>
        <p className="text-[9px] text-sky-500/80 pt-0.5">Solo seguimiento · no afecta el Total General</p>
      </div>
    </div>
  );
}

const nodeTypes = { flujo: FlujoNode, text: TextNode, op: OpNode, total: TotalNode, embed: EmbedNode, cambio: CambioNode };

function GroupMovementsModal({ group, onChange, onClose }) {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const movements = group.movements || [];
  const saveMovement = (m) => {
    const exists = movements.some((x) => x.id === m.id);
    onChange(exists ? movements.map((x) => (x.id === m.id ? m : x)) : [...movements, m]);
    setFormOpen(false); setEditing(null);
  };
  const removeMovement = (mid) => { if (window.confirm("¿Eliminar este movimiento?")) onChange(movements.filter((x) => x.id !== mid)); };
  const sorted = movements.slice().sort((a, b) => parseLocalDate(b.fecha) - parseLocalDate(a.fecha));
  return (
    <div className="fixed inset-0 z-[85] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="board-group-modal">
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-medium text-lg flex items-center gap-2"><Wallet size={18} className="text-orange-500" /> {group.title || "Mapa de flujo"}</h3>
          <button onClick={onClose} className={ICON_BTN} data-testid="board-group-close"><X size={16} /></button>
        </div>
        <button onClick={() => { setEditing(null); setFormOpen(true); }} className={`${BTN_PRIMARY} w-full mb-4`} data-testid="board-group-add-movement"><Plus size={16} /> Nuevo movimiento</button>
        {sorted.length === 0 ? <p className="text-sm text-slate-500 text-center py-6">Sin movimientos aún.</p> : (
          <div className="flex flex-col gap-2">
            {sorted.map((m) => (
              <div key={m.id} className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 p-2" data-testid={`board-group-mov-${m.id}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${m.tipo === "ingreso" ? "bg-emerald-500" : "bg-rose-500"}`} />
                <div className="flex-1 min-w-0"><p className="font-body text-xs truncate">{m.concepto}</p><p className="font-mono2 text-[10px] text-slate-400">{fmtMoney(m.monto, m.moneda)} · {parseLocalDate(m.fecha).toLocaleDateString("es-VE")}</p></div>
                <button onClick={() => { setEditing(m); setFormOpen(true); }} className="p-1 text-slate-400 hover:text-indigo-500" data-testid={`board-group-edit-${m.id}`}><Pencil size={13} /></button>
                <button onClick={() => removeMovement(m.id)} className="p-1 text-slate-400 hover:text-rose-500" data-testid={`board-group-delmov-${m.id}`}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      {formOpen && <MovementForm initial={editing} onSave={saveMovement} onCancel={() => { setFormOpen(false); setEditing(null); }} />}
    </div>
  );
}

function DocPicker({ existingIds, onPick, onClose }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    api.get("/notes").then(({ data }) => setDocs(data.filter((n) => DOC_META[n.type]))).catch(() => setDocs([]));
  }, []);
  return (
    <div className="fixed inset-0 z-[85] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="board-doc-picker">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-medium text-lg">Incrustar documento existente</h3>
          <button onClick={onClose} className={ICON_BTN}><X size={16} /></button>
        </div>
        {!docs ? <p className="text-sm text-slate-400">Cargando...</p> : docs.length === 0 ? <p className="text-sm text-slate-500">No tienes documentos para incrustar.</p> : (
          <div className="space-y-2">
            {docs.map((d) => {
              const meta = DOC_META[d.type]; const Icon = meta.Icon; const used = existingIds.includes(d.id);
              return (
                <button key={d.id} onClick={() => onPick(d)} disabled={used} className="w-full text-left flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 p-3 hover:border-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`board-doc-pick-${d.id}`}>
                  <Icon size={16} style={{ color: meta.color }} className="shrink-0" />
                  <span className="text-sm truncate flex-1">{d.title}</span>
                  <span className="text-[10px] text-slate-400 shrink-0">{used ? "incrustado" : meta.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function InsertMenu({ onAdd }) {
  const [open, setOpen] = useState(false);
  const item = "w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg";
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} className={BTN_PRIMARY} data-testid="board-insert-btn"><Plus size={16} /> Insertar <ChevronDown size={14} /></button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute z-[61] mt-1 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl p-1.5" data-testid="board-insert-menu">
            <p className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wide text-slate-400">Bloques de cálculo</p>
            <button onClick={() => { onAdd("flujo"); setOpen(false); }} className={item} data-testid="insert-flujo"><Wallet size={16} className="text-orange-500" /> Mapa de Flujo Económico</button>
            <button onClick={() => { onAdd("ledger"); setOpen(false); }} className={item} data-testid="insert-ledger"><Scale size={16} className="text-emerald-500" /> Mapa Contable</button>
            <button onClick={() => { onAdd("op"); setOpen(false); }} className={item} data-testid="insert-op"><Sigma size={16} className="text-indigo-500" /> Operación (Σ)</button>
            <button onClick={() => { onAdd("cambio"); setOpen(false); }} className={item} data-testid="insert-cambio"><ArrowLeftRight size={16} className="text-sky-500" /> Nodo Cambiario (cambio de moneda)</button>
            <div className="my-1 border-t border-slate-100 dark:border-slate-800" />
            <p className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-wide text-slate-400">Otros</p>
            <button onClick={() => { onAdd("embed"); setOpen(false); }} className={item} data-testid="insert-embed"><Link2 size={16} className="text-slate-500" /> Incrustar documento existente</button>
            <button onClick={() => { onAdd("text"); setOpen(false); }} className={item} data-testid="insert-text"><Type size={16} className="text-amber-500" /> Nota de texto</button>
            <div className="px-3 py-1.5 text-[10px] text-slate-400 italic">Presupuesto y Proyecto: próximamente</div>
          </div>
        </>
      )}
    </div>
  );
}

function Editor({ id }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const [rate, setRate] = useState(0);
  const [blocks, setBlocks] = useState([]);
  const [edges, setEdges] = useState([]);
  const [totalPos, setTotalPos] = useState({ x: 680, y: 220 });
  const [history, setHistory] = useState([]);
  const [embedData, setEmbedData] = useState({}); // docId -> doc
  const [saving, setSaving] = useState(false);
  const [movGroupId, setMovGroupId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(() => localStorage.getItem("notacora_board_help3") !== "0");

  useEffect(() => {
    api.get(`/notes/${id}`).then(({ data }) => {
      setTitle(data.title);
      setFolderId(data.folder_id || "");
      const b = data.board || {};
      setDisplayCurrency(b.displayCurrency || "USD");
      if (b.rate) setRate(b.rate);
      setBlocks(b.blocks || []);
      setEdges(b.edges || []);
      if (b.totalPos) setTotalPos(b.totalPos);
    }).catch((err) => { toast.error(formatApiError(err.response?.data?.detail)); navigate("/"); });
  }, [id, navigate]);

  useEffect(() => {
    api.get("/rates").then(({ data }) => { const u = (data.assets || []).find((a) => a.code === "usd_bcv"); if (u?.rate) setRate((p) => p || u.rate); }).catch(() => {});
    api.get("/rates/history").then(({ data }) => setHistory(data.history || [])).catch(() => {});
  }, []);

  // Fetch de documentos incrustados
  const embedIds = useMemo(() => blocks.filter((b) => b.kind === "embed").map((b) => b.docId), [blocks]);
  useEffect(() => {
    for (const docId of embedIds) {
      if (embedData[docId]) continue;
      api.get(`/notes/${docId}`).then(({ data }) => setEmbedData((prev) => ({ ...prev, [docId]: data }))).catch(() => {});
    }
  }, [embedIds]); // eslint-disable-line

  // Totales por bloque (interfaz común {totalIn,totalEg,haber,count?})
  const totals = useMemo(() => {
    const out = {};
    for (const b of blocks) {
      if (b.kind === "flujo") {
        out[b.id] = computeTotals(b.movements || [], { displayCurrency, rate, history });
      } else if (b.kind === "embed") {
        const doc = embedData[b.docId];
        if (!doc) continue;
        if (b.docType === "movemap") {
          out[b.id] = computeTotals((doc.movemap || {}).movements || [], { displayCurrency, rate, history });
        } else if (b.docType === "ledger") {
          const l = doc.ledger || {};
          const { byType, patrimonio } = computeLedger(l.accounts || [], l.entries || [], { displayCurrency, rate });
          out[b.id] = { totalIn: byType.ingreso, totalEg: byType.gasto, haber: patrimonio };
        }
      }
    }
    return out;
  }, [blocks, embedData, displayCurrency, rate, history]);

  const numericBlocks = useMemo(() => blocks.filter((b) => totals[b.id]), [blocks, totals]);

  const membersOf = useCallback((targetId) => {
    const ids = edges.filter((e) => e.target === targetId).map((e) => e.source);
    return blocks.filter((b) => ids.includes(b.id) && totals[b.id]);
  }, [edges, blocks, totals]);

  const grandTotal = useMemo(() => {
    const members = membersOf(TOTAL_ID);
    let totalIn = 0, totalEg = 0;
    for (const b of members) { const t = totals[b.id]; totalIn += t.totalIn; totalEg += t.totalEg; }
    return { totalIn, totalEg, haber: totalIn - totalEg, count: members.length };
  }, [membersOf, totals]);

  // --- Historial Deshacer/Rehacer ---
  const blocksRef = useRef([]); useEffect(() => { blocksRef.current = blocks; }, [blocks]);
  const edgesRef = useRef([]); useEffect(() => { edgesRef.current = edges; }, [edges]);
  const totalPosRef = useRef(totalPos); useEffect(() => { totalPosRef.current = totalPos; }, [totalPos]);
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const editTimer = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const syncHist = useCallback(() => { setCanUndo(pastRef.current.length > 0); setCanRedo(futureRef.current.length > 0); }, []);
  const snapshot = useCallback(() => {
    pastRef.current.push({ blocks: blocksRef.current, edges: edgesRef.current, totalPos: totalPosRef.current });
    if (pastRef.current.length > 60) pastRef.current.shift();
    futureRef.current = [];
    syncHist();
  }, [syncHist]);
  // Agrupa ediciones inline (tipear en un input) en un solo snapshot por sesión.
  const beginEdit = useCallback(() => {
    if (editTimer.current) { clearTimeout(editTimer.current); } else { snapshot(); }
    editTimer.current = setTimeout(() => { editTimer.current = null; }, 800);
  }, [snapshot]);
  const applyState = useCallback((s) => { setBlocks(s.blocks); setEdges(s.edges); setTotalPos(s.totalPos); }, []);
  const undo = useCallback(() => {
    if (!pastRef.current.length) return;
    editTimer.current = null;
    futureRef.current.push({ blocks: blocksRef.current, edges: edgesRef.current, totalPos: totalPosRef.current });
    applyState(pastRef.current.pop());
    syncHist();
  }, [applyState, syncHist]);
  const redo = useCallback(() => {
    if (!futureRef.current.length) return;
    pastRef.current.push({ blocks: blocksRef.current, edges: edgesRef.current, totalPos: totalPosRef.current });
    applyState(futureRef.current.pop());
    syncHist();
  }, [applyState, syncHist]);
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  const updateBlock = useCallback((bid, patch) => { beginEdit(); setBlocks((bs) => bs.map((b) => (b.id === bid ? { ...b, ...patch } : b))); }, [beginEdit]);
  const removeBlock = useCallback((bid) => {
    snapshot();
    setBlocks((bs) => bs.filter((b) => b.id !== bid));
    setEdges((es) => es.filter((e) => e.source !== bid && e.target !== bid));
  }, [snapshot]);
  const duplicateBlock = useCallback((bid) => {
    snapshot();
    setBlocks((bs) => {
      const src = bs.find((b) => b.id === bid);
      if (!src) return bs;
      const copy = JSON.parse(JSON.stringify(src));
      copy.id = uid();
      copy.x = (src.x || 0) + 40; copy.y = (src.y || 0) + 40;
      if (copy.title) copy.title = `${copy.title} (copia)`;
      if (copy.concepto) copy.concepto = `${copy.concepto} (copia)`;
      return [...bs, copy];
    });
  }, [snapshot]);
  const openMovements = useCallback((bid) => setMovGroupId(bid), []);

  const posFor = (n) => ({ x: 60 + (n % 3) * 300, y: 60 + Math.floor(n / 3) * 240 });

  const addInsert = async (kind) => {
    if (kind === "embed") { setPickerOpen(true); return; }
    snapshot();
    if (kind === "flujo") {
      setBlocks((bs) => { const n = bs.length; return [...bs, { id: uid(), kind: "flujo", title: `Mapa ${bs.filter((x) => x.kind === "flujo").length + 1}`, movements: [], ...posFor(n) }]; });
    } else if (kind === "text") {
      setBlocks((bs) => [...bs, { id: uid(), kind: "text", text: "", ...posFor(bs.length) }]);
    } else if (kind === "op") {
      setBlocks((bs) => [...bs, { id: uid(), kind: "op", operation: "subtract", field: "haber", ...posFor(bs.length) }]);
    } else if (kind === "cambio") {
      setBlocks((bs) => [...bs, { id: uid(), kind: "cambio", concepto: "", monto: 0, moneda: "USD", montoDestino: 0, monedaDestino: "VES", ...posFor(bs.length) }]);
    } else if (kind === "embed") {
      setPickerOpen(true);
    } else if (kind === "ledger") {
      try {
        const { data } = await api.post("/notes", { type: "ledger", title: "Mapa Contable", ledger: { displayCurrency, rate, accounts: [], entries: [] }, folder_id: folderId || null });
        setEmbedData((prev) => ({ ...prev, [data.id]: data }));
        setBlocks((bs) => [...bs, { id: uid(), kind: "embed", docType: "ledger", docId: data.id, title: "Mapa Contable", ...posFor(bs.length) }]);
        toast.success('Mapa Contable creado. Ábrelo con "Abrir para editar".');
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }
  };

  const pickDoc = (doc) => {
    snapshot();
    setEmbedData((prev) => ({ ...prev, [doc.id]: doc }));
    setBlocks((bs) => [...bs, { id: uid(), kind: "embed", docType: doc.type, docId: doc.id, title: doc.title, ...posFor(bs.length) }]);
    setPickerOpen(false);
  };

  const nodes = useMemo(() => {
    const arr = blocks.map((b) => ({ id: b.id, type: b.kind, position: { x: b.x || 0, y: b.y || 0 }, data: b }));
    if (numericBlocks.length > 0) arr.push({ id: TOTAL_ID, type: "total", position: totalPos, data: {} });
    return arr;
  }, [blocks, numericBlocks.length, totalPos]);

  const displayEdges = useMemo(() => edges.map((e) => {
    const color = e.target === TOTAL_ID ? "#10b981" : (blocks.find((b) => b.id === e.target)?.kind === "op" ? "#6366f1" : "#94a3b8");
    return { ...e, type: "smoothstep", animated: e.target === TOTAL_ID, style: { stroke: color, strokeWidth: 2 }, markerEnd: { type: "arrowclosed", color } };
  }), [edges, blocks]);

  const onConnect = useCallback((params) => {
    if (params.source === params.target) return;
    setEdges((es) => {
      if (es.some((e) => e.source === params.source && e.target === params.target)) { toast.info("Esa conexión ya existe."); return es; }
      snapshot();
      return addEdge({ id: `${params.source}->${params.target}`, source: params.source, target: params.target, sourceHandle: "out", targetHandle: "in" }, es);
    });
  }, [snapshot]);
  const onEdgeClick = useCallback((evt, edge) => { evt.stopPropagation(); if (window.confirm("¿Quitar esta conexión?")) { snapshot(); setEdges((es) => es.filter((e) => e.id !== edge.id)); } }, [snapshot]);
  const onNodeDragStop = useCallback((_e, node) => { if (node.id === TOTAL_ID) { beginEdit(); setTotalPos({ x: node.position.x, y: node.position.y }); } else updateBlock(node.id, { x: node.position.x, y: node.position.y }); }, [updateBlock, beginEdit]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/notes/${id}`, { title: title || "Pizarra", board: { displayCurrency, rate, blocks, edges, totalPos }, folder_id: folderId || null });
      toast.success("Guardado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const dismissHelp = () => { setShowHelp(false); localStorage.setItem("notacora_board_help3", "0"); };
  const doDuplicate = async (mode) => {
    setDupOpen(false);
    try {
      let newBlocks = blocks;
      if (mode === "blank") {
        // Clonar documentos incrustados (movemap/ledger) con saldos/movimientos en cero.
        const idMap = {};
        for (const b of blocks) {
          if (b.kind === "embed" && (b.docType === "movemap" || b.docType === "ledger") && !idMap[b.docId]) {
            const doc = embedData[b.docId];
            if (!doc) continue;
            let payload;
            if (b.docType === "movemap") {
              const mv = doc.movemap || {};
              payload = { type: "movemap", title: doc.title, movemap: { displayCurrency: mv.displayCurrency || "USD", rate: mv.rate || 0, movements: [] }, folder_id: folderId || null };
            } else {
              const l = doc.ledger || {};
              const accounts = (l.accounts || []).map((a) => ({ ...a, opening: 0 }));
              payload = { type: "ledger", title: doc.title, ledger: { displayCurrency: l.displayCurrency || "USD", rate: l.rate || 0, accounts, entries: [] }, folder_id: folderId || null };
            }
            const { data } = await api.post("/notes", payload);
            idMap[b.docId] = data.id;
          }
        }
        newBlocks = blocks.map((b) => {
          if (b.kind === "flujo") return { ...b, movements: [] };
          if (b.kind === "cambio") return { ...b, monto: 0, montoDestino: 0 };
          if (b.kind === "embed" && idMap[b.docId]) return { ...b, docId: idMap[b.docId] };
          return b;
        });
      }
      const { data } = await api.post("/notes", { type: "board", title: `${title || "Pizarra"} (copia)`, board: { displayCurrency, rate, blocks: newBlocks, edges, totalPos }, folder_id: folderId || null });
      toast.success(mode === "blank" ? "Pizarra duplicada en blanco (plantilla lista)" : "Pizarra duplicada con sus datos");
      window.open(`/pizarra/${data.id}`, "_blank");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const movGroup = blocks.find((b) => b.id === movGroupId && b.kind === "flujo") || null;
  const bgColor = theme === "dark" ? "#1e293b" : "#e2e8f0";
  const ctxVal = { totals, displayCurrency, blocks, updateBlock, removeBlock, duplicateBlock, grandTotal, openMovements, membersOf };

  return (
    <div className={`${PAGE} font-body flex flex-col h-screen`}>
      <Navbar />
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex-wrap">
        <button onClick={() => navigate("/")} className={ICON_BTN} data-testid="board-back"><ArrowLeft size={16} /></button>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="font-heading font-medium text-lg bg-transparent border-none focus:outline-none focus:ring-0 min-w-[140px] flex-1" placeholder="Pizarra" data-testid="board-title" />
        <FolderSelect value={folderId} onChange={setFolderId} allowNew />
        <div className="flex items-center gap-1 border border-slate-200 dark:border-slate-800 rounded-lg p-0.5">
          {["USD", "VES"].map((c) => (
            <button key={c} onClick={() => setDisplayCurrency(c)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${displayCurrency === c ? "bg-indigo-600 text-white" : "text-slate-500"}`} data-testid={`board-currency-${c}`}>{CURRENCY_SYMBOL[c]}</button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="font-mono2 text-[11px] text-slate-500">Tasa</span>
          <input type="number" value={rate || ""} onChange={(e) => setRate(Number(e.target.value))} className="w-24 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-md px-2 py-1 text-sm" placeholder="Bs/$" data-testid="board-rate" />
        </div>
        <InsertMenu onAdd={addInsert} />
        <button onClick={undo} disabled={!canUndo} className={`${ICON_BTN} disabled:opacity-40`} title="Deshacer (Ctrl+Z)" data-testid="board-undo"><Undo2 size={16} /></button>
        <button onClick={redo} disabled={!canRedo} className={`${ICON_BTN} disabled:opacity-40`} title="Rehacer (Ctrl+Y)" data-testid="board-redo"><Redo2 size={16} /></button>
        <button onClick={() => setDupOpen(true)} className={ICON_BTN} title="Duplicar esta Pizarra (para reusar como plantilla)" data-testid="board-duplicate"><CopyPlus size={16} /></button>
        <button onClick={save} disabled={saving} className={BTN_PRIMARY} data-testid="board-save"><Save size={16} /> {saving ? "..." : "Guardar"}</button>
      </div>

      {showHelp && (
        <div className="flex items-start gap-2 px-4 py-2.5 bg-indigo-50 dark:bg-indigo-500/10 border-b border-indigo-100 dark:border-indigo-500/20 text-xs text-indigo-800 dark:text-indigo-200" data-testid="board-help">
          <Info size={15} className="shrink-0 mt-0.5" />
          <p className="flex-1"><b>¿Cómo funciona?</b> Usa <b>"Insertar"</b> para agregar bloques (Flujo Económico, Mapa Contable, Nota, Operación) o <b>incrustar un documento que ya tengas</b>. Luego <b>arrastra un cable</b> desde el punto derecho de cada bloque hasta el <b>Total General</b> (suma) o a una <b>Operación</b>. Toca un cable para quitarlo.</p>
          <button onClick={dismissHelp} className="shrink-0 text-indigo-500 hover:text-indigo-700" data-testid="board-help-close"><X size={14} /></button>
        </div>
      )}

      <div className="flex-1 relative">
        <BoardCtx.Provider value={ctxVal}>
          <ReactFlow nodes={nodes} edges={displayEdges} nodeTypes={nodeTypes} onConnect={onConnect} onEdgeClick={onEdgeClick} onNodeDragStop={onNodeDragStop} fitView proOptions={{ hideAttribution: true }} minZoom={0.2}>
            <Background gap={22} size={1.5} color={bgColor} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </BoardCtx.Provider>
        {blocks.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center pointer-events-auto max-w-sm">
              <Landmark size={40} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <p className="font-heading text-lg mb-1">Pizarra vacía</p>
              <p className="font-body text-sm text-slate-500 mb-4">Usa <b>"Insertar"</b> para agregar Mapas de Flujo, Mapas Contables, notas o incrustar documentos que ya tengas, y combínalos con cables hacia el <b>Total General</b>.</p>
            </div>
          </div>
        )}
      </div>

      {movGroup && <GroupMovementsModal group={movGroup} onChange={(movs) => updateBlock(movGroup.id, { movements: movs })} onClose={() => setMovGroupId(null)} />}
      {pickerOpen && <DocPicker existingIds={embedIds} onPick={pickDoc} onClose={() => setPickerOpen(false)} />}
      {dupOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4" onClick={() => setDupOpen(false)} data-testid="board-dup-modal">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100 dark:border-slate-800">
              <CopyPlus size={18} className="text-indigo-500" />
              <h3 className="font-heading font-semibold text-base flex-1">Duplicar Pizarra</h3>
              <button onClick={() => setDupOpen(false)} className="text-slate-400 hover:text-slate-600" data-testid="board-dup-close"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-slate-500">¿Cómo quieres crear la copia?</p>
              <button onClick={() => doDuplicate("keep")} className="w-full text-left rounded-xl border border-slate-200 dark:border-slate-700 hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 px-4 py-3 transition" data-testid="board-dup-keep">
                <p className="font-heading font-medium text-sm">Conservar datos</p>
                <p className="text-xs text-slate-500 mt-0.5">Copia idéntica: misma estructura y todos los montos, movimientos y saldos.</p>
              </button>
              <button onClick={() => doDuplicate("blank")} className="w-full text-left rounded-xl border border-slate-200 dark:border-slate-700 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 px-4 py-3 transition" data-testid="board-dup-blank">
                <p className="font-heading font-medium text-sm">Empezar en blanco</p>
                <p className="text-xs text-slate-500 mt-0.5">Mantiene la estructura (bloques, cuentas y conexiones) pero pone en cero movimientos, saldos y cambios. Ideal como plantilla mensual.</p>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Board() {
  const { id } = useParams();
  return <ReactFlowProvider><Editor id={id} /></ReactFlowProvider>;
}
